import { MonitorStatus } from '@fe/core';
import { createDropdownList } from '../app/components/inputs/DropdownListWithValueField';
import {
    DateDelimitersEnum,
    DateTimeFormatEnum,
    DistanceUnitsEnum,
    FlightFobVolumeDisplayUnit,
    FlightFobWeightDisplayUnit,
    FlightListFilterMode,
    LatLongDisplayFormatEnum,
    MapGotoTarget,
    MapLanguageMode,
    OnMapControlsDisplayEnum,
    StarmapType,
    TimeDisplayFormat,
    TimeDisplayZone,
    WeatherLegendDateTimeDisplay
} from '../common/enums';
import { DropdownItems } from '../components/weather/settings/settings-options';
import { MyFleetAcidListSource, MyFleetAirportModeEnum, MyFleetMode, UDViewTerrainShadingType } from '../udView/udview';
import {
    AirportIdDisplayStyleEnum,
    AirportMonitorSortType,
    EliDisplayModeEnum,
    EventListBackgroundColorMode,
    FleetMonitorSortType,
    MapAutoScreenshotTarget,
    MapImageFormat,
    MapUpdateMode,
    SortOrderEnum
} from './settings';
import { CompanyThresholdsMode, MonitorThresholdAlertMinimumLevel, ThresholdEnableState, ThresholdLevelActionType } from './thresholds';

export const FleetMonitorSortOptions: DropdownItems<FleetMonitorSortType> = [
    { value: FleetMonitorSortType.monitorStatusPriorityCallsignFlightStatus, text: 'Monitor status + Priority + Callsign + Flight Status' },
    { value: FleetMonitorSortType.callsignFlightStatus, text: 'Callsign + Flight Status' },
    { value: FleetMonitorSortType.flightStatusCallsign, text: 'Flight Status + Callsign' },
    { value: FleetMonitorSortType.flightStatusDepartureTime, text: 'Flight Status + Departure time' },
    { value: FleetMonitorSortType.departureTime, text: 'Departure time' },
    { value: FleetMonitorSortType.eta, text: 'ETA' },
    { value: FleetMonitorSortType.distanceFromDestination, text: 'Distance from destination' },
    { value: FleetMonitorSortType.departureTimeEta, text: 'Departure time & ETA' },
    { value: FleetMonitorSortType.taxiTime, text: 'Taxi time' }
];

export const AirportMonitorSortOptions: DropdownItems<AirportMonitorSortType> = [
    { value: AirportMonitorSortType.monitorStatusPriorityId, text: 'Monitor status + Priority + ID' },
    { value: AirportMonitorSortType.id, text: 'ID' }
];

export const SortDirectionOptions: DropdownItems<SortOrderEnum> = [
    { value: SortOrderEnum.asc, text: 'Ascending' },
    { value: SortOrderEnum.desc, text: 'Descending' }
];

export const MonitorStatusOptions: DropdownItems<MonitorStatus> = [
    { value: MonitorStatus.null, text: 'None' },
    { value: MonitorStatus.clear, text: 'Clear' },
    { value: MonitorStatus.info, text: 'Info' },
    { value: MonitorStatus.watch, text: 'Watch' },
    { value: MonitorStatus.warning, text: 'Warning' },
    { value: MonitorStatus.critical, text: 'Critical' },
    { value: MonitorStatus.emergency, text: 'Emergency' }
];

export const FobWeightDisplayOptions = createDropdownList<FlightFobWeightDisplayUnit>()([
    { value: FlightFobWeightDisplayUnit.Pound, text: 'lb' },
    { value: FlightFobWeightDisplayUnit.Kilogram, text: 'kg' }
]);

export const OnMapControlsDisplayOptions = createDropdownList<OnMapControlsDisplayEnum>()([
    { value: OnMapControlsDisplayEnum.Show, text: 'Show' },
    { value: OnMapControlsDisplayEnum.OnlyWhenActive, text: 'Show on hover' },
    { value: OnMapControlsDisplayEnum.Hide, text: 'Hide' }
]);

export const FobVolumeDisplayOptions = createDropdownList<FlightFobVolumeDisplayUnit>()([
    { value: FlightFobVolumeDisplayUnit.Gallon, text: 'gal' },
    { value: FlightFobVolumeDisplayUnit.Liter, text: 'ltr' }
]);

export const TimeZoneOptions = createDropdownList<TimeDisplayZone>()([
    { value: TimeDisplayZone.Utc, text: 'UTC' },
    { value: TimeDisplayZone.Local, text: 'Local' }
]);

export const TimeFormatOptions = createDropdownList<TimeDisplayFormat>()([
    { value: TimeDisplayFormat.H24, text: '24 H' },
    { value: TimeDisplayFormat.H12, text: '12 H' }
]);

export const DateFormatOptions = createDropdownList<DateTimeFormatEnum>()([
    { value: DateTimeFormatEnum.MonthDayYear, text: 'MM/DD/YYYY' },
    { value: DateTimeFormatEnum.YearMonthDay, text: 'YYYY/MM/DD' },
    { value: DateTimeFormatEnum.DayMonthYear, text: 'DD/MM/YYYY' }
]);

export const DateDelimiterOptions = createDropdownList<DateDelimitersEnum>()([
    { value: DateDelimitersEnum.Slash, text: '/ (slash)' },
    { value: DateDelimitersEnum.Dot, text: '. (dot)' },
    { value: DateDelimitersEnum.Dash, text: '- (dash)' }
]);

export const DistanceUnitsOptions = createDropdownList<DistanceUnitsEnum>()([
    { value: DistanceUnitsEnum.NauticalMiles, text: 'Nautical Miles (NM)' },
    { value: DistanceUnitsEnum.StatuteMiles, text: 'Statute Miles (SM)' },
    { value: DistanceUnitsEnum.Kilometers, text: 'Kilometers (KM)' }
]);

export const LatLongDisplayFormatOptions = createDropdownList<LatLongDisplayFormatEnum>()([
    { value: LatLongDisplayFormatEnum.SignedDecimalDegrees, text: 'Signed decimal degrees (23.4567, -123.4567)' },
    { value: LatLongDisplayFormatEnum.UnsignedDecimalDegrees, text: 'Decimal degrees (23.4567N, 123.4567W)' },
    { value: LatLongDisplayFormatEnum.DegreesAndDecimalMinutes, text: 'Degrees and decimal minutes (N23 27.402, W123 27.402)' },
    {
        value: LatLongDisplayFormatEnum.DegreesMinutesDecimalSeconds,
        text: 'Degrees, minutes and decimal seconds (23 27 24.12N, 123 27 24.12W)'
    }
]);

export const NavDataUpdateModeOptions = createDropdownList<MapUpdateMode>()([
    { value: MapUpdateMode.automatic, text: 'Automatically stay current' },
    { value: MapUpdateMode.manual, text: 'Require user confirmation to switch to newer navigation data' }
]);

export const MapImageFormatOptions = createDropdownList<MapImageFormat>()([
    { value: MapImageFormat.png, text: 'png' },
    { value: MapImageFormat.jpg, text: 'jpg' },
    { value: MapImageFormat.webp, text: 'webp' }
]);

export const MyFleetModeOptions = createDropdownList<MyFleetMode>()([
    { value: MyFleetMode.disabled, text: 'Disabled' },
    { value: MyFleetMode.airline, text: 'Airline' },
    { value: MyFleetMode.airport, text: 'Airport' },
    { value: MyFleetMode.acid, text: 'ACID list' }
]);

export const MyFleetAirportModeOptions = createDropdownList<MyFleetAirportModeEnum>()([
    { value: MyFleetAirportModeEnum.departureArrivals, text: 'Departures & Arrivals' },
    { value: MyFleetAirportModeEnum.arrivals, text: 'Arrivals' },
    { value: MyFleetAirportModeEnum.departures, text: 'Departures' }
]);

export const MyFleetAcidListSourceOptions = createDropdownList<MyFleetAcidListSource>()([
    { value: MyFleetAcidListSource.manual, text: 'Manual' },
    { value: MyFleetAcidListSource.file, text: 'File' },
    { value: MyFleetAcidListSource.desktopInterface, text: 'Desktop interface', disabled: true }
]);

export const ThresholdsCompanyModeOptions = createDropdownList<CompanyThresholdsMode>()([
    { value: CompanyThresholdsMode.notUsed, text: 'Company not used' },
    { value: CompanyThresholdsMode.template, text: 'Company as template' },
    { value: CompanyThresholdsMode.minimum, text: 'Company as minimum' },
    { value: CompanyThresholdsMode.specified, text: 'Company as specified' }
]);

export const ThresholdLevelActionTypeOptions = createDropdownList<ThresholdLevelActionType>()([
    { value: ThresholdLevelActionType.none, text: 'None' },
    { value: ThresholdLevelActionType.possible, text: 'Possible' },
    { value: ThresholdLevelActionType.withinMinutes, text: 'Within X minutes' },
    { value: ThresholdLevelActionType.immediate, text: 'Immediate' }
]);

export const ThresholdEnableStateOptions = createDropdownList<ThresholdEnableState>()([
    { value: ThresholdEnableState.notSet, text: 'Not set' },
    { value: ThresholdEnableState.enabled, text: 'Enabled' },
    { value: ThresholdEnableState.disabled, text: 'Disabled' }
]);

export const AirportIdDisplayStyleOptions = createDropdownList<AirportIdDisplayStyleEnum>()([
    { value: AirportIdDisplayStyleEnum.iata, text: 'IATA' },
    { value: AirportIdDisplayStyleEnum.icao, text: 'ICAO' },
    { value: AirportIdDisplayStyleEnum.iataIcao, text: 'IATA/ICAO' },
    { value: AirportIdDisplayStyleEnum.icaoIata, text: 'ICAO/IATA' }
]);

export const WeatherLegendDateTimeDisplayOptions = createDropdownList<WeatherLegendDateTimeDisplay>()([
    { value: WeatherLegendDateTimeDisplay.none, text: 'None' },
    { value: WeatherLegendDateTimeDisplay.timeOnly, text: 'Time only' },
    { value: WeatherLegendDateTimeDisplay.timeDay, text: 'Time/day' },
    { value: WeatherLegendDateTimeDisplay.timeDate, text: 'Time/date' }
]);

export const EliOpenInModeOptions = createDropdownList<EliDisplayModeEnum>()([
    { value: EliDisplayModeEnum.newTab, text: 'New Tab' },
    { value: EliDisplayModeEnum.newWindow, text: 'New Window' }
]);

export const MapGotoTargetOptions = createDropdownList<MapGotoTarget>()([
    { value: MapGotoTarget.active, text: 'Active map' },
    { value: MapGotoTarget.all, text: 'All maps' },
    { value: MapGotoTarget.map0, text: 'Only map #1' },
    { value: MapGotoTarget.map1, text: 'Only map #2' },
    { value: MapGotoTarget.map2, text: 'Only map #3' },
    { value: MapGotoTarget.map3, text: 'Only map #4' }
]);

export const FlightListFilterModeOptions = createDropdownList<FlightListFilterMode>()([
    { value: FlightListFilterMode.view, text: 'View' },
    { value: FlightListFilterMode.myFleet, text: 'My fleet' },
    { value: FlightListFilterMode.all, text: 'All' }
]);

export const MapLanguageOptions = createDropdownList<MapLanguageMode>()([
    { value: MapLanguageMode.english, text: 'English' },
    { value: MapLanguageMode.device, text: 'Device language' },
    { value: MapLanguageMode.localLatin, text: 'Local (latin)' },
    { value: MapLanguageMode.localNative, text: 'Local (native)' },
    { value: MapLanguageMode.bilingual, text: 'Bilingual' }
]);

export const MonitorThresholdAlertMinimumLevelOptions = [
    { value: null, text: 'Inherit' },
    { value: MonitorThresholdAlertMinimumLevel.info, text: 'Info' },
    { value: MonitorThresholdAlertMinimumLevel.watch, text: 'Watch' },
    { value: MonitorThresholdAlertMinimumLevel.warning, text: 'Warning' },
    { value: MonitorThresholdAlertMinimumLevel.critical, text: 'Critical' },
    { value: MonitorThresholdAlertMinimumLevel.emergency, text: 'Emergency' }
];

export const StarmapTypeOptions = createDropdownList<StarmapType>()([
    { value: StarmapType.basic, text: 'Dark sky (stars only)' },
    { value: StarmapType.enhanced, text: 'Deep sky' }
]);

export const MapAutoScreenshotTargetOptions = createDropdownList<MapAutoScreenshotTarget>()([
    { value: MapAutoScreenshotTarget.upload, text: 'Upload to server' },
    { value: MapAutoScreenshotTarget.file, text: 'Save to file' },
    { value: MapAutoScreenshotTarget.all, text: 'Upload & save to file' }
]);

export const EventListBackgroundColorModeOptions = createDropdownList<EventListBackgroundColorMode>()([
    { value: EventListBackgroundColorMode.default, text: 'Default' },
    { value: EventListBackgroundColorMode.importantBackground, text: 'Highlight background based on event color' },
    { value: EventListBackgroundColorMode.monitorStatusBackground, text: 'Highlight background based on monitor status' }
]);

export const TerrainShadingTypeOptions = createDropdownList<UDViewTerrainShadingType>()([
    { value: UDViewTerrainShadingType.singleColor, text: 'Single elevation' },
    { value: UDViewTerrainShadingType.colorRamp, text: 'Multiple elevations' },
]);